import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { requestMessageTranslation } from 'dashboard/api/messageTranslation';

const STATUSES = new Set([
  'complete',
  'pending',
  'failed',
  'skipped',
  'untranslated',
]);

// No provider error text is rendered here. All failures have a safe UI label.
function failureLabel(code) {
  if (code === 'invalid_target_language') {
    return '翻译失败：结果不是中文，请重试。';
  }
  if (code === 'retry_cooldown') {
    return '翻译暂不可用，请稍后重试。';
  }
  if (['stale_source', 'stale_cache'].includes(code)) {
    return '';
  }
  if (code === 'not_authorized') return '无权翻译此消息。';
  if (code === 'message_unavailable') return '消息已不可用。';
  return '翻译失败，请重试。';
}

function normalizeState(value) {
  if (!value || !STATUSES.has(value.status) || value.target !== 'zh_CN') {
    return null;
  }
  return {
    status: value.status,
    code: typeof value.code === 'string' ? value.code : '',
    canRetry: (value.can_retry ?? value.canRetry) === true,
    retryAfter: Math.max(
      0,
      Math.min(3600, Number(value.retry_after ?? value.retryAfter) || 0)
    ),
    content: typeof value.content === 'string' ? value.content : null,
    translation:
      typeof value.translation === 'string' ? value.translation : null,
  };
}

export function useMessageTranslation(
  context,
  cachedTranslation,
  options = {}
) {
  const route = useRoute();
  const localResult = ref(null);
  const busy = ref(false);
  const now = ref(Date.now());
  const retryAt = ref(0);
  let generation = 0;
  let countdown = null;
  let disposed = false;

  const attributes = computed(() => context.contentAttributes.value || {});
  const eligible = computed(
    () =>
      (options.sourceReady?.value ?? true) &&
      [0, 1].includes(context.messageType.value) &&
      !context.isPrivate?.value &&
      !context.private?.value &&
      !attributes.value.deleted &&
      !attributes.value.hidden &&
      (
        attributes.value.borderchatTranslation ||
        attributes.value.borderchat_translation
      )?.code !== 'not_translatable' &&
      /[\p{L}\p{N}]/u.test(
        options.sourceText?.value ?? context.content.value ?? ''
      )
  );
  const scope = computed(() =>
    JSON.stringify([
      route.params.accountId,
      context.conversationId.value,
      context.id.value,
      context.content.value,
      options.sourceKey?.value,
    ])
  );
  const projectedState = computed(() =>
    normalizeState(
      attributes.value.borderchatTranslation ||
        attributes.value.borderchat_translation
    )
  );
  const state = computed(
    () =>
      (projectedState.value?.status === 'skipped'
        ? projectedState.value
        : localResult.value) ||
      projectedState.value || {
        status: cachedTranslation.value ? 'complete' : 'untranslated',
        canRetry: true,
        retryAfter: 0,
        code: '',
      }
  );
  const translation = computed(() => {
    if (state.value.status !== 'complete') return null;
    return (
      localResult.value?.translation ||
      localResult.value?.content ||
      cachedTranslation.value
    );
  });
  const retrySeconds = computed(() =>
    Math.max(0, Math.ceil((retryAt.value - now.value) / 1000))
  );
  // can_retry describes the server's current instant. A positive retry_after
  // permits another explicit request after the cooldown/lease has elapsed;
  // the server still verifies the lease and authorization on every request.
  const showRequest = computed(
    () =>
      ['failed', 'pending', 'untranslated'].includes(state.value.status) &&
      (state.value.canRetry ||
        (state.value.retryAfter > 0 &&
          ['pending', 'failed'].includes(state.value.status)))
  );
  const pending = computed(
    () =>
      !['complete', 'skipped'].includes(state.value.status) &&
      (busy.value ||
        (state.value.status === 'pending' &&
          (!showRequest.value || retrySeconds.value > 0)))
  );
  const canRequest = computed(
    () =>
      eligible.value && !busy.value && !retrySeconds.value && showRequest.value
  );
  const notice = computed(() => {
    if (pending.value) return '正在翻译…';
    if (state.value.status === 'pending') return '翻译尚未完成，可重试。';
    if (state.value.status === 'failed') return failureLabel(state.value.code);
    if (state.value.status === 'skipped') return '';
    return '';
  });
  const requestLabel = computed(() => {
    if (busy.value) return '正在翻译…';
    if (retrySeconds.value) return `${retrySeconds.value} 秒后可重试`;
    return state.value.status === 'untranslated' ? '翻译为中文' : '重新翻译';
  });

  function stopCountdown() {
    if (countdown !== null) clearInterval(countdown);
    countdown = null;
  }

  function startCooldown(seconds) {
    stopCountdown();
    now.value = Date.now();
    retryAt.value = now.value + seconds * 1000;
    if (!seconds) return;
    countdown = setInterval(() => {
      now.value = Date.now();
      if (!retrySeconds.value) stopCountdown();
    }, 1000);
  }

  watch(
    scope,
    () => {
      generation += 1;
      localResult.value = null;
      busy.value = false;
      startCooldown(projectedState.value?.retryAfter || 0);
    },
    { flush: 'sync' }
  );

  const projectionKey = computed(() =>
    JSON.stringify([projectedState.value, cachedTranslation.value])
  );
  watch(
    projectionKey,
    () => {
      // The authoritative message update supersedes only a finished request.
      if (busy.value) return;
      localResult.value = null;
      startCooldown(projectedState.value?.retryAfter || 0);
    },
    { immediate: true }
  );

  async function requestTranslation() {
    if (!canRequest.value) return;
    const requestGeneration = generation;
    const requestScope = scope.value;
    const requestProjection = projectionKey.value;
    const accountId = route.params.accountId;
    const conversationId = context.conversationId.value;
    const messageId = context.id.value;
    busy.value = true;
    const stillCurrent = () =>
      !disposed &&
      eligible.value &&
      requestGeneration === generation &&
      requestScope === scope.value;
    const keepConfirmedProjection = () => {
      if (projectionKey.value === requestProjection) return false;
      const confirmed =
        projectedState.value?.status === 'skipped' ||
        (projectedState.value?.status === 'complete' &&
          cachedTranslation.value?.trim());
      if (!confirmed) return false;
      // A message update can confirm completion before a delayed pending/error
      // HTTP response arrives. Keep that accepted result rather than hiding it.
      localResult.value = null;
      startCooldown(projectedState.value.retryAfter);
      return true;
    };
    try {
      const result = normalizeState(
        await requestMessageTranslation({
          accountId,
          conversationId,
          messageId,
        })
      );
      if (!stillCurrent()) return;
      if (keepConfirmedProjection())
        return projectedState.value.status === 'complete';
      // A complete response must actually contain its validated translation.
      if (
        !result ||
        (result.status === 'complete' &&
          !(result.translation || result.content)?.trim())
      ) {
        throw new Error('Invalid translation response');
      }
      localResult.value = result;
      startCooldown(result.retryAfter);
      return result.status === 'complete';
    } catch (error) {
      if (!stillCurrent()) return;
      if (keepConfirmedProjection())
        return projectedState.value.status === 'complete';
      const result = normalizeState(error.response?.data);
      const unavailable = [401, 403, 404].includes(error.response?.status);
      localResult.value =
        result && result.status !== 'complete'
          ? result
          : {
              status: 'failed',
              code: unavailable
                ? error.response.status === 404
                  ? 'message_unavailable'
                  : 'not_authorized'
                : 'request_failed',
              canRetry: !unavailable,
              retryAfter: unavailable ? 0 : 5,
            };
      startCooldown(localResult.value.retryAfter);
    } finally {
      if (
        !disposed &&
        requestGeneration === generation &&
        requestScope === scope.value
      ) {
        busy.value = false;
      }
    }
  }

  onUnmounted(() => {
    disposed = true;
    generation += 1;
    stopCountdown();
  });

  return {
    eligible,
    state,
    translation,
    busy,
    pending,
    showRequest,
    canRequest,
    notice,
    requestLabel,
    requestTranslation,
  };
}
